import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Search, Filter, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, AlertTriangle, Info, Loader2, RefreshCw, Activity
} from "lucide-react";
import Pagination from "@/components/Pagination";
import TransactionAnalyzerModal from "@/components/TransactionAnalyzerModal";

const STATUS_ICONS = {
  success: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  error: <XCircle className="w-4 h-4 text-red-500" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
  info: <Info className="w-4 h-4 text-blue-500" />,
};

const STATUS_BG = {
  success: "bg-green-50 border-green-200",
  error: "bg-red-50 border-red-200",
  warning: "bg-amber-50 border-amber-200",
  info: "bg-blue-50 border-blue-200",
};

const ROLE_BADGE = {
  admin: "bg-indigo-100 text-indigo-700",
  dealer: "bg-purple-100 text-purple-700",
  customer: "bg-sky-100 text-sky-700",
};

function LogEntry({ log }) {
  const [open, setOpen] = useState(false);
  const ts = log.timestamp?.replace("T", " ").slice(0, 19);

  return (
    <div className={`border rounded-lg mb-2 transition-all ${STATUS_BG[log.status] || "bg-white border-slate-200"}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 flex items-center gap-3"
      >
        {STATUS_ICONS[log.status] || STATUS_ICONS.info}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-800">{log.event_label}</span>
            {log.role && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${ROLE_BADGE[log.role] || "bg-slate-100 text-slate-600"}`}>
                {log.role}
              </span>
            )}
            {log.dth_id && (
              <span className="text-xs font-mono text-slate-400">VC: {log.dth_id}</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{log.message}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-slate-400 font-mono">{ts}</p>
          {log.amount != null && (
            <p className="text-xs font-semibold text-slate-600">₹{Number(log.amount).toFixed(2)}</p>
          )}
        </div>
        {(log.request_data || log.response_data) ? (
          open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        ) : <div className="w-4" />}
      </button>
      {open && (log.request_data || log.response_data) && (
        <div className="px-4 pb-3 border-t border-slate-200/50 space-y-2">
          {log.request_data && (
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Request</p>
              <pre className="text-xs bg-white/80 rounded p-2 overflow-x-auto font-mono text-slate-600 max-h-40 overflow-y-auto">
                {JSON.stringify(log.request_data, null, 2)}
              </pre>
            </div>
          )}
          {log.response_data && (
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Response</p>
              <pre className="text-xs bg-white/80 rounded p-2 overflow-x-auto font-mono text-slate-600 max-h-40 overflow-y-auto">
                {JSON.stringify(log.response_data, null, 2)}
              </pre>
            </div>
          )}
          {log.sim_id && (
            <p className="text-[10px] text-slate-400">SIM ID: <span className="font-mono">{log.sim_id.slice(0, 8)}</span></p>
          )}
          <p className="text-[10px] text-slate-400">Recharge ID: <span className="font-mono">{log.recharge_id}</span></p>
        </div>
      )}
    </div>
  );
}

export default function RechargeLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");

  const [analyzeTxnId, setAnalyzeTxnId] = useState("");
  const [analyzeInput, setAnalyzeInput] = useState("");

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', PAGE_SIZE);
      if (search) params.set('search', search);
      if (statusFilter !== 'All') params.set('status', statusFilter);
      if (roleFilter !== 'All') params.set('role', roleFilter);
      
      const res = await api.get(`/admin/recharge-logs?${params.toString()}`);
      setLogs(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, search, statusFilter, roleFilter]);

  // Group by recharge_id (display only)
  const grouped = {};
  logs.forEach((l) => {
    if (!grouped[l.recharge_id]) grouped[l.recharge_id] = [];
    grouped[l.recharge_id].push(l);
  });

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <FileText className="w-6 h-6 text-brand-primary" /> Recharge Logs
          </h3>
          <p className="text-slate-500 text-sm mt-1">End-to-end recharge pipeline logs. Click any entry to view request/response details.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:underline disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Transaction Analyzer Search */}
      <div className="bg-gradient-to-r from-brand-primary/5 to-brand-accent/5 border border-brand-primary/20 rounded-xl p-4 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div>
          <h4 className="font-bold text-brand-nav flex items-center gap-2">
            <Activity className="w-5 h-5 text-brand-primary" />
            Transaction Pipeline Analyzer
          </h4>
          <p className="text-xs text-slate-500 mt-1">Deep-dive into a specific transaction's complete lifecycle (Logs, Webhooks, Ledgers).</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (analyzeInput.trim()) setAnalyzeTxnId(analyzeInput.trim()); }} className="flex w-full md:w-auto gap-2">
          <input
            type="text"
            placeholder="Enter Txn No, Order ID..."
            value={analyzeInput}
            onChange={(e) => setAnalyzeInput(e.target.value)}
            className="flex-1 md:w-64 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
          />
          <button type="submit" disabled={!analyzeInput.trim()} className="bg-brand-primary hover:bg-brand-primaryHover text-white px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50 transition">
            Analyze
          </button>
        </form>
      </div>

      {analyzeTxnId && (
        <TransactionAnalyzerModal
          txnId={analyzeTxnId}
          role="admin"
          onClose={() => { setAnalyzeTxnId(""); setAnalyzeInput(""); }}
        />
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search logs, DTH ID, recharge ID..." value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="All">All Status</option>
          <option value="success">✅ Success</option>
          <option value="error">❌ Error</option>
          <option value="info">ℹ️ Info</option>
          <option value="warning">⚠️ Warning</option>
        </select>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="All">All Roles</option>
          <option value="admin">Admin</option>
          <option value="dealer">Dealer</option>
          <option value="customer">Customer</option>
        </select>
      </div>

      <p className="text-xs text-slate-400 mb-3">{total} log entries | Page {page} of {pageCount}</p>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-xl">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No recharge logs found.</p>
        </div>
      ) : (
        <>
          <div className="space-y-1">
            {logs.map((l) => (
              <LogEntry key={l.id} log={l} />
            ))}
          </div>
          <div className="mt-3">
            <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
          </div>
        </>
      )}
    </div>
  );
}
